import { useCallback, useEffect, useRef, useState } from 'react';
import { useColor } from 'react-color-palette';
import _ from 'lodash';
import {
  useUpdatedColors,
  useUpdatedCustomizationInfo,
  useCustomImages,
} from '../../api/Brand';
import { useCustomTheme } from '../../contexts/useCustomTheme';
import THEME from '../../config/theme';
import { firestore } from '../../services/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

// Hook da tela "App Mobile" (Personalização > App Mobile).
// Reúne o que antes vivia espalhado em EditMain (toggle + cores exclusivas
// Android/iOS) e em CustomLogin (tela de abertura do app mobile), já que as
// duas coisas só afetam o app mobile e fazem mais sentido juntas.
export const useAppMobile = () => {
  const [show, setShow] = useState({
    showTabBackgroundColor: false,
    showTabIcondColor: false,
    showTabHighlightColor: false,
    showStatusBarColor: false,
  });
  const handleShowPicker = useCallback((field, value) => {
    setShow((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  });

  const {
    customColor: colorInfo,
    customInfo: customizationInfo,
    isLoadingColorInfo,
    isLoadingCustomInfo,
  } = useCustomTheme();

  const { mutateAsync: updatedColor } = useUpdatedColors();

  const [tabBackgroundColor, setTabBackgroundColor] = useColor(THEME.COLORS.BACKGROUND_MAIN);
  const [tabIcondColor, setTabIcondColor] = useColor(THEME.COLORS.PRIMARY_700);
  const [tabHighlightColor, setTabHighlightColor] = useColor(THEME.COLORS.TEXT_MAIN);
  const [statusBarColor, setStatusBarColor] = useColor('#FFFFFF');

  const [loadingColorsApp, setLoadingColorsApp] = useState(false);
  const [isSavedColorsApp, setIsSavedColorsApp] = useState(false);

  // Baseline das cores conforme salvas no banco. Usado para saber se há
  // alterações não salvas em andamento (e só então mostrar o aviso de "Salvar").
  const [savedColors, setSavedColors] = useState(null);

  // Ativa automaticamente o switch quando já existir personalização salva,
  // para o admin não achar que perdeu a configuração ao reabrir a tela.
  const [hasHydratedSwitch, setHasHydratedSwitch] = useState(false);

  // Busca as cores já salvas no banco.
  useEffect(() => {
    if (colorInfo && Object.keys(colorInfo).length > 1) {
      const loadedTabBackground = colorInfo.tabNavigator?.background || THEME.COLORS.BACKGROUND_MAIN;
      const loadedTabIcon = colorInfo.tabNavigator?.icon || THEME.COLORS.PRIMARY_700;
      const loadedTabHighlight = colorInfo.tabNavigator?.highlight || THEME.COLORS.TEXT_MAIN;
      const loadedStatusBar = colorInfo.background?.statusBar || '#FFFFFF';

      setTabBackgroundColor({ ...tabBackgroundColor, hex: loadedTabBackground });
      setTabIcondColor({ ...tabIcondColor, hex: loadedTabIcon });
      setTabHighlightColor({ ...tabHighlightColor, hex: loadedTabHighlight });
      setStatusBarColor({ ...statusBarColor, hex: loadedStatusBar });

      // Baseline: as cores acabaram de vir do banco, então não há alterações pendentes.
      setSavedColors({
        tabBackground: loadedTabBackground,
        tabIcon: loadedTabIcon,
        tabHighlight: loadedTabHighlight,
        statusBar: loadedStatusBar,
      });

      if (!hasHydratedSwitch) {
        setHasHydratedSwitch(true);
      }
    }
  }, [colorInfo]);

  const debouncedUpdateColor = useCallback(
    _.debounce((newColors) => {
      updatedColor(newColors);
    }, 500),
    []
  );

  const updateAppColors = async () => {
    try {
      setLoadingColorsApp(true);
      const docRef = doc(firestore, 'customization', 'colors');
      const docSnapshot = await getDoc(docRef);

      if (docSnapshot.exists()) {
        const currentData = docSnapshot.data();
        const updatedData = {
          tabNavigator: {
            background: tabBackgroundColor.hex,
            icon: tabIcondColor.hex,
            highlight: tabHighlightColor.hex,
          },
          background: {
            ...currentData.background,
            statusBar: statusBarColor.hex,
          },
        };

        await updateDoc(docRef, updatedData);

        // Novo baseline após salvar: some o aviso de alterações pendentes.
        setSavedColors({
          tabBackground: tabBackgroundColor.hex,
          tabIcon: tabIcondColor.hex,
          tabHighlight: tabHighlightColor.hex,
          statusBar: statusBarColor.hex,
        });

        setLoadingColorsApp(false);
        setIsSavedColorsApp(true);
      } else {
        setLoadingColorsApp(false);
      }
    } catch (error) {
      console.error('Erro ao atualizar as cores:', error);
      setLoadingColorsApp(false);
    }
  };

  // TEXTO SOBRE A IMAGEM DE ABERTURA DO APP MOBILE
  const [textAbout, setTextAbout] = useState('');
  const { mutateAsync: updatedCustomInfo } = useUpdatedCustomizationInfo();

  const debounceText = useCallback(
    _.debounce((text) => {
      updatedCustomInfo(text);
    }, 500),
    []
  );

  // Semeado uma ÚNICA vez: o cache de "infos" é invalidado a cada salvamento
  // (ver api/Brand), e re-semear a cada refetch devolveria ao campo a versão
  // anterior do texto no meio da digitação.
  const hasSeededTextAbout = useRef(false);
  useEffect(() => {
    if (!customizationInfo || hasSeededTextAbout.current) return;
    hasSeededTextAbout.current = true;
    setTextAbout(customizationInfo.textAbout || '');
  }, [customizationInfo]);

  useEffect(() => {
    const infos = { textAbout };
    if (Object.values(infos).some((value) => value !== undefined)) {
      debounceText(infos);
    }
  }, [textAbout]);

  // Há alterações de cor não salvas em andamento? (compara com o baseline salvo)
  const normHex = (h) => (h || '').toLowerCase();
  const hasUnsavedColorChanges =
    savedColors != null &&
    (normHex(tabBackgroundColor.hex) !== normHex(savedColors.tabBackground) ||
      normHex(tabIcondColor.hex) !== normHex(savedColors.tabIcon) ||
      normHex(tabHighlightColor.hex) !== normHex(savedColors.tabHighlight) ||
      normHex(statusBarColor.hex) !== normHex(savedColors.statusBar));

  // Imagens usadas na pré-visualização dos 3 mockups (abertura, login e início).
  const { data: imageAbout } = useCustomImages('imageAbout');
  const { data: logoAbout } = useCustomImages('logoAbout');
  const { data: logoAuth } = useCustomImages('logoAuth');

  return {
    show,
    handleShowPicker,
    tabBackgroundColor,
    setTabBackgroundColor,
    tabIcondColor,
    setTabIcondColor,
    tabHighlightColor,
    setTabHighlightColor,
    statusBarColor,
    setStatusBarColor,
    updateAppColors,
    loadingColorsApp,
    isSavedColorsApp,
    setIsSavedColorsApp,
    hasUnsavedColorChanges,
    isLoading: isLoadingColorInfo || isLoadingCustomInfo,
    textAbout,
    setTextAbout,
    colorInfo,
    imageAbout,
    logoAbout,
    logoAuth,
  };
};
